'use client';

import Link from 'next/link';
import { 
  ArrowRight, 
  Sparkles, 
  TrendingUp, 
  Award,
  ShieldCheck,
  Leaf,
  ChefHat,
  Users,
  Star,
  Clock,
  MapPin,
  Map
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { GangwonMap } from '@/components/GangwonMap';
import { TrendingRecipeCarousel } from '@/components/TrendingRecipeCarousel';

// 더미 데이터
const seasonalProducts = [
  { id: 1, name: '강원도 감자', price: '15,000', unit: '5kg', badge: '이달의 BEST', image: '🥔', discount: '-20%' },
  { id: 2, name: '평창 고랭지 배추', price: '8,000', unit: '1포기', badge: '신선도 1위', image: '🥬', discount: '-15%' },
  { id: 3, name: '홍천 찰옥수수', price: '12,000', unit: '10개입', badge: '재구매율 1위', image: '🌽', discount: null },
  { id: 4, name: '양구 시래기', price: '18,000', unit: '1kg', badge: '프리미엄', image: '🥗', discount: '-10%' },
  { id: 5, name: '횡성 한우', price: '89,000', unit: '600g', badge: '명품인증', image: '🥩', discount: null },
  { id: 6, name: '속초 오징어', price: '25,000', unit: '5마리', badge: '당일배송', image: '🦑', discount: '-25%' },
];

const trendingRecipes = [
  { id: 1, title: '감자전 황금레시피', category: '전통요리', time: '30분', difficulty: '쉬움', image: '🥔' },
  { id: 2, title: '쟁반비빔막국수', category: '면요리', time: '35분', difficulty: '보통', image: '🍜' },
  { id: 3, title: '춘천식 닭갈비', category: '구이요리', time: '40분', difficulty: '보통', image: '🍗' },
  { id: 4, title: '황태구이 맛집스타일', category: '생선요리', time: '25분', difficulty: '쉬움', image: '🐟' },
  { id: 5, title: '곰취나물 산채비빔밥', category: '전통요리', time: '50분', difficulty: '어려움', image: '🥗' },
  { id: 6, title: '옥수수 크림스프', category: '양식', time: '25분', difficulty: '쉬움', image: '🌽' },
  { id: 7, title: '인제 황태해장국', category: '찌개/국', time: '45분', difficulty: '보통', image: '🍲' },
  { id: 8, title: '평창 감자옹심이', category: '전통요리', time: '60분', difficulty: '어려움', image: '🥟' },
  { id: 9, title: '횡성 한우 불고기', category: '구이요리', time: '35분', difficulty: '보통', image: '🥩' },
  { id: 10, title: '속초 아바이순대', category: '전통요리', time: '90분', difficulty: '어려움', image: '🌭' },
  { id: 11, title: '강릉 초당순두부', category: '전통요리', time: '20분', difficulty: '쉬움', image: '🥛' },
  { id: 12, title: '정선 곤드레밥', category: '전통요리', time: '55분', difficulty: '보통', image: '🍚' }
];

const farmStories = [
  { 
    id: 1, 
    farm: '평창 행복농장', 
    title: '3대째 이어온 감자 농사 이야기', 
    location: '강원도 평창군',
    verified: true,
    rating: 4.8
  },
  { 
    id: 2, 
    farm: '횡성 청정한우', 
    title: '자연방목으로 키운 건강한 한우', 
    location: '강원도 횡성군',
    verified: true,
    rating: 4.9
  },
  { 
    id: 3, 
    farm: '속초 바다농장', 
    title: '동해 청정해역의 신선한 수산물', 
    location: '강원도 속초시',
    verified: true,
    rating: 4.7
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-blue-50"
        data-onboarding="hero"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full opacity-20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 px-4 py-1.5 bg-green-100 text-green-700 border-green-200">
              <Sparkles className="w-3 h-3 mr-1" />
              강원도 제철 특산물 플랫폼
            </Badge>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              신선한 강원도를<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                집에서 만나보세요
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              농가 직송 제철 특산물과 AI가 추천하는 맞춤 레시피로
              건강하고 맛있는 식탁을 완성하세요
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/seasonal">
                <Button size="lg" className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Leaf className="w-5 h-5 mr-2" />
                  제철 특산물 보기
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link href="/recipes">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg rounded-xl border-2 hover:bg-gray-50">
                  <ChefHat className="w-5 h-5 mr-2" />
                  AI 레시피 탐색
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
              <div className="flex items-center gap-2 text-gray-600">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">농가 직거래 인증</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Award className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium">품질 보증 시스템</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium">10,000+ 만족 고객</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gangwon Map Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <Badge className="mb-1 bg-blue-100 text-blue-700 border-blue-200">
              <Map className="w-3 h-3 mr-1" />
              지역별 특산품
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
              강원도 지역별 특산품 지도
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              각 지역을 클릭하여 특별한 특산품들을 만나보세요
            </p>
          </div>
          <GangwonMap />
        </div>
      </section>

      {/* Trending Recipes Carousel */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-6">
            <Badge className="mb-2 bg-purple-100 text-purple-700 border-purple-200">
              <TrendingUp className="w-3 h-3 mr-1" />
              TRENDING
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              인기 급상승 레시피
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              AI가 추천하는 제철 재료 활용 레시피를 확인해보세요
            </p>
          </div>

          <TrendingRecipeCarousel recipes={trendingRecipes} />
        </div>
      </section>

      {/* Farm Stories Preview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              STORY
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              농가 이야기
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              정직하게 재배하는 강원도 농가들의 진짜 이야기를 들어보세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {farmStories.map((story) => (
              <Card key={story.id} className="hover:shadow-lg transition-all cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg mb-1">{story.farm}</CardTitle>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-3 h-3 mr-1" />
                        {story.location}
                      </div>
                    </div>
                    {story.verified && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        인증
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3">{story.title}</p>
                  <div className="flex items-center">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-semibold ml-1">{story.rating}</span>
                    </div>
                    <span className="text-sm text-gray-500 ml-2">신뢰도 평점</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link href="/farmers">
              <Button variant="outline" size="lg" className="rounded-full">
                더 많은 농가 스토리 보기
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            지금 바로 시작하세요
          </h2>
          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">
            신선한 강원도 제철 특산물과 AI 레시피로 건강한 식탁을 만들어보세요
          </p>
          <Link href="/seasonal">
            <Button size="lg" className="bg-white text-green-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-xl shadow-lg">
              제철 특산물 둘러보기
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
